<template>
  <div class="consultation-container">
    <div v-if="loading" class="overlay">
      <clip-loader />
    </div>

    <Alert
      v-if="error"
      :data-variant="errorVariant"
      :data-heading="errorHeading"
    >
      {{ error }}
    </Alert>

    <CdrFlagBanner
      v-for="(flag, index) in examination?.pendingCdrFlags ?? []"
      :key="index"
      v-bind="flag"
    />

    <div v-if="examination && product && !loading" id="product">
      <div
        class="tw-sticky tw-top-[--mobile-header-height] xl:-tw-top-[--main-content-padding-top] tw-bg-white tw-z-[2]"
      >
        <examinations-header
          :examination-id="examination.id"
          :product-definition="product"
          :total-examinations="totalExaminations"
          :patient="patient"
          @mount-image-compare="mountImageCompare"
          @initialize-print="printCase"
        />
      </div>

      <chat-container
        v-if="
          examination &&
          productHasPermission(PRODUCT_PERMISSIONS.USE_CONSENSUS, product)
        "
        :edit-save-uri="'/api/internal/' + examination.id + '/channels'"
        :chat-notifications="chatNotifications"
        @update-top-navigation="updateChatNotifications"
      />

      <div class="tab-content">
        <router-view
          :patient="patient"
          :examination="examination"
          :product="product"
          :primary-center-id="primaryCenterId"
        />
      </div>
    </div>
    <VueJsDialogMixinWrapper
      v-if="!!showPrintDialogWithOptions"
      :data-component="showPrintDialogComponent"
      :data-options="showPrintDialogWithOptions"
      @close="closePrintCase"
    />
  </div>
</template>
<script setup>
import VueJsDialogMixinWrapper from "../../../mixins/VueJsDialogMixinWrapper.vue";
import Alert from "../../../design-system/Alert.vue";
import CdrFlagBanner from "../../ClinicalDataRemoval/CdrFlagBanner.vue";
import ClipLoader from "../../Utils/ClipLoader.vue";
</script>
<script>
import ImageCompare from "../AddOns/ImageCompare.vue";
import ChatContainer from "../AddOns/Chat/ChatContainer.vue";
import CustomPrintViewDialog from "../Examination/ExaminationComponents/components/PrintCaseView.vue";
import ExaminationsHeader from "./ExaminationComponents/components/ExaminationsHeader.vue";
import Examination from "./Examination.vue";
import ConsultationMedia from "../AddOns/ConsultationMedia.vue";
import UserErrorMessage from "../../../mixins/UserErrorMessage.vue";
import { fetchProduct, productHasPermission } from "../../../helpers/product";
import { PRODUCT_PERMISSIONS } from "../../../helpers/definitions";

export default {
  components: {
    ImageCompare,
    ExaminationsHeader,
    ConsultationMedia,
    Examination,
    ChatContainer,
  },
  mixins: [UserErrorMessage],
  data() {
    return {
      loading: true,
      examination: null,
      patient: null,
      chatNotifications: null,
      product: null,
      comments: {},
      user: {},
      backButtonUri: "/",
      actions: {
        isPrioritized: false,
        isFlagged: false,
        isBookmarked: false,
      },
      images: {},
      totalExaminations: null,
      PRODUCT_PERMISSIONS: PRODUCT_PERMISSIONS,
      productHasPermission: productHasPermission,
      showPrintDialogComponent: CustomPrintViewDialog,
      showPrintDialogWithOptions: null,
      error: null,
      errorVariant: "danger",
      errorHeading: "Error",
    };
  },
  computed: {
    primaryCenterId() {
      return this.$store.state.user.primaryCenter.id;
    },
  },
  watch: {
    "$store.state.currentExamination.examinationData": function (updated) {
      if (updated?.pendingCdrFlags !== undefined) {
        this.examination = {
          ...this.examination,
          pendingCdrFlags: updated.pendingCdrFlags,
        };
      }
    },
  },
  mounted: function () {
    this.loadData();
    this.$store.commit("setCurrentExamination", this.$route.params.id);
  },
  methods: {
    loadProduct: async function (productId) {
      const product = await fetchProduct(
        productId,
        this.examination.centerId,
        this.$store,
      );
      this.product = product || null;
    },
    loadData: async function () {
      try {
        const productId = await this.loadExamination();
        await this.loadProduct(productId);
        await this.getTotalExaminations();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loading = false;
      }

      localStorage.setItem(
        "last_visited_examination_id",
        this.$route.params.id,
      );
    },
    handleError(error) {
      const errorMessage = this.getErrorMessage(error.response?.status);
      this.error = errorMessage.message;
      this.errorVariant = errorMessage.variant;
      this.errorHeading = errorMessage.heading;
    },
    getErrorMessage(status) {
      if (status === 404) {
        return {
          message: this.trans("errorMessages.404ErrorConsultation"),
          variant: "warning",
          heading: "404",
        };
      }

      return {
        message: this.trans("errorMessages.HTTPStatus.XXX"),
        variant: "danger",
        heading: status ? status.toString() : "Error",
      };
    },
    loadExamination: async function () {
      try {
        const response = await axios.get(
          `/api/internal/tumour-wound/${this.$route.params.id}`,
        );

        if (!response?.data?.examination?.patient) {
          return null;
        }

        const examination = response.data.examination;
        const { age, fullName, gender, ssn } = examination.patient;
        const { centerId, notifications, type } = examination;

        this.examination = examination;

        this.patient = {
          age,
          centerId,
          fullName,
          gender,
          notifications,
          ssn,
        };

        return type?.id || null;
      } catch (error) {
        this.userErrorMessage?.(error);
        return null;
      }
    },
    getTotalExaminations: async function () {
      const response = await axios.get(
        "/api/internal/image-comparer/count-examinations/" +
          this.$route.params.id +
          "?type=" +
          this.product.meta.id,
      );
      this.totalExaminations = response?.data?.total_examinations || null;
    },
    updateChatNotifications: function (notifications) {
      this.chatNotifications = notifications;
    },
    printCase: function () {
      this.showPrintDialogWithOptions = {
        data: {
          uri: `/api/internal/examinations/${this.examination.id}/print`,
          caseNo: this.examination.caseNo,
          productDefinition: this.product,
        },
      };
    },
    closePrintCase() {
      this.showPrintDialogWithOptions = null;
    },
  },
};
</script>
